/**
 * CapacityAddPersonRow.tsx
 * The trailing "add person" row of the merged "Team Allocation & Weekly
 * Capacity" grid (staffing-allocation follow-up 2026-07-13). Replaces the old
 * per-week roster column's add control. Picks a person (ordered least-loaded
 * first — D6 "suggest who's free") and a Full 100% / Mentor 25% quick-add mode,
 * then adds them to the plan across every week of the source at that %; you then
 * tune each week with the inline steppers above.
 *
 * Colors via theme.schedule.* tokens — no hex literals (CODING-STANDARDS).
 */

import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Popper,
  type PopperProps,
  TableCell,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { useTheme } from '@mui/material/styles';

import { EMP_COL_W, ROLE_COL_W } from './phase2CapacityShared';
import {
  MENTOR_DEFAULT_PERCENT,
  FULL_ALLOCATION_PERCENT,
  REVIEWER_DEFAULT_PERCENT,
  APPROVER_DEFAULT_PERCENT,
} from '../../../../utils/scheduleAssignmentDefaults';
import { orderBySpareCapacity, spareCapacityHint } from '../../../../utils/rosterAllocation';
import { allocationBand } from '../../../../utils/scheduleAllocationBands';
import type { AssignableUser } from '../../../../services/scheduleAssignments';

export interface CapacityAddPersonRowProps {
  candidates: AssignableUser[];
  /** Lowercased userKeys already shown as rows (excluded from the picker). */
  existingKeys: Set<string>;
  /** Lowercased userKey → the person's PEAK weekly utilization % (for ordering + hint). */
  allocByUser: Map<string, number>;
  /** Colspan of the trailing (week) cell = weekColumns.length × 7. */
  weekColSpan: number;
  /** Add a person to the plan at the chosen quick-add %. */
  onAdd: (userKey: string, displayName: string, percent: number) => void;
  /**
   * Gutter colspan — the legacy grid's gutter is Employee + Role (2 cells); the
   * roster timeline's gutter is a single Employee column (1). Defaults to 2.
   */
  gutterColSpan?: number;
  /** Gutter cell width — defaults to the legacy Employee + Role width. */
  gutterWidth?: number;
}

interface Candidate {
  userKey: string;
  displayName: string;
  allocationPercent: number;
}

/**
 * The picker sits in the narrow sticky gutter, but the OPTION list needs room
 * for "Firstname Lastname  ·  100%  at capacity" on one line (2026-07-26
 * feedback: names wrapped to two truncated lines). The popper takes its own
 * width instead of inheriting the input's.
 */
const PICKER_POPUP_W = 340;
const WidePopper: React.FC<PopperProps> = (props) => (
  <Popper {...props} style={{ ...props.style, width: PICKER_POPUP_W }} placement="bottom-start" />
);

/**
 * Quick-add roles (Jul-23 meeting, built 2026-07-26): each preloads a typical
 * share; the % field beside the toggles stays EDITABLE — "10 or 25 or 50,
 * depends on the project" (Gigi). Approver at a thin 5% exists mainly so their
 * availability shows on the plan (the "Ian's away" early warning).
 */
type AddMode = 'full' | 'mentor' | 'reviewer' | 'approver';
const MODE_DEFAULT: Record<AddMode, number> = {
  full: FULL_ALLOCATION_PERCENT,
  mentor: MENTOR_DEFAULT_PERCENT,
  reviewer: REVIEWER_DEFAULT_PERCENT,
  approver: APPROVER_DEFAULT_PERCENT,
};
const MODE_TIP: Record<AddMode, string> = {
  full: 'Hands-on — 100% of their week while the job runs',
  mentor: 'Supervising a junior (L3+ skill gate) — light oversight share',
  reviewer: 'Ongoing review of results — dial the % to the project size',
  approver: 'Final approval — a thin share so their availability is visible on this plan',
};

export const CapacityAddPersonRow: React.FC<CapacityAddPersonRowProps> = ({
  candidates,
  existingKeys,
  allocByUser,
  weekColSpan,
  onAdd,
  gutterColSpan = 2,
  gutterWidth = EMP_COL_W + ROLE_COL_W,
}) => {
  const theme = useTheme();
  const [mode, setMode] = useState<AddMode>('full');
  // The editable share — re-seeded whenever a mode is picked.
  const [percentText, setPercentText] = useState<string>(String(FULL_ALLOCATION_PERCENT));
  const pickMode = (v: AddMode) => { setMode(v); setPercentText(String(MODE_DEFAULT[v])); };
  const effectivePercent = (): number => {
    const parsed = Math.round(Number(percentText));
    return Number.isFinite(parsed) && parsed > 0 ? Math.min(parsed, 500) : MODE_DEFAULT[mode];
  };

  const options = useMemo<Candidate[]>(() => {
    const list = candidates
      .filter((u) => !existingKeys.has(u.AppUserKey.toLowerCase()))
      .map((u) => ({
        userKey: u.AppUserKey,
        displayName: u.DisplayName,
        allocationPercent: allocByUser.get(u.AppUserKey.toLowerCase()) ?? 0,
      }));
    return orderBySpareCapacity(list);
  }, [candidates, existingKeys, allocByUser]);

  const handleAdd = (cand: Candidate | null) => {
    if (!cand) return;
    onAdd(cand.userKey, cand.displayName, effectivePercent());
  };

  return (
    <TableRow>
      {/* Gutter cell (Employee + Role width) — the add control, sticky-left. */}
      <TableCell
        colSpan={gutterColSpan}
        sx={{
          position: 'sticky',
          left: 0,
          zIndex: 1,
          backgroundColor: theme.palette.background.paper,
          boxShadow: `inset -1px 0 0 0 ${theme.border.color}`,
          borderRight: `1px solid ${theme.border.color}`,
          py: 0.75,
          px: 1.5,
          width: gutterWidth,
          verticalAlign: 'middle',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              value={mode}
              exclusive
              size="small"
              onChange={(_e, v: AddMode | null) => { if (v) pickMode(v); }}
              aria-label="Add mode"
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', py: 0.1, px: 0.6, fontSize: '0.62rem' } }}
            >
              {(Object.keys(MODE_DEFAULT) as AddMode[]).map((m) => (
                <ToggleButton key={m} value={m} title={MODE_TIP[m]}>
                  {m === 'full' ? 'Full' : m.charAt(0).toUpperCase() + m.slice(1)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <TextField
              size="small"
              value={percentText}
              onChange={(e) => setPercentText(e.target.value.replace(/[^\d]/g, ''))}
              inputProps={{ inputMode: 'numeric', 'aria-label': 'Share percent' }}
              title="Their share of each week — editable (10 / 25 / 50… whatever the project needs)"
              sx={{ width: 58, '& .MuiInputBase-input': { fontSize: '0.68rem', py: 0.25, px: 0.5, textAlign: 'right' } }}
              InputProps={{ endAdornment: <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', pl: 0.25 }}>%</Typography> }}
            />
          </Box>
          <Autocomplete<Candidate, false, false, false>
            options={options}
            getOptionLabel={(o) => o.displayName}
            value={null}
            blurOnSelect
            clearOnBlur
            onChange={(_e, val) => handleAdd(val)}
            PopperComponent={WidePopper}
            renderOption={(props, o) => {
              const band = allocationBand(o.allocationPercent);
              const bandColor =
                band === 'over' ? theme.schedule.allocOver
                  : band === 'at' ? theme.schedule.allocAt
                    : band === 'healthy' ? theme.schedule.allocHealthy
                      : theme.schedule.allocUnder;
              return (
                <Box component="li" {...props} key={o.userKey} title={o.displayName} sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 0.6 }}>
                  {/* Band dot + single-line name; the load block keeps one line on the right. */}
                  <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: bandColor, flexShrink: 0 }} />
                  <Typography noWrap sx={{ fontSize: '0.8rem', flex: 1, minWidth: 0 }}>
                    {o.displayName}
                  </Typography>
                  <Typography component="span" noWrap sx={{ fontSize: '0.68rem', color: 'text.secondary', flexShrink: 0 }}>
                    <Box component="b" sx={{ color: bandColor, fontVariantNumeric: 'tabular-nums', mr: 0.5 }}>
                      {Math.round(o.allocationPercent)}%
                    </Box>
                    {spareCapacityHint(o.allocationPercent)}
                  </Typography>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Add person…"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <PersonAddAlt1Icon sx={{ fontSize: 16, color: 'text.disabled', ml: 0.5, mr: 0.25 }} />,
                }}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.72rem', py: 0.4 } }}
              />
            )}
          />
        </Box>
      </TableCell>

      {/* Trailing hint spanning the week columns. */}
      <TableCell colSpan={weekColSpan} sx={{ py: 0.75, px: 1.5, verticalAlign: 'middle' }}>
        <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>
          Adds the person to every week at the chosen % — pick a role for a typical share (Mentor 25 · Reviewer 10 · Approver 5) or type your own. Tune each week with the steppers above; over 100% is allowed (advisory).
        </Typography>
      </TableCell>
    </TableRow>
  );
};
